import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import { aggregateLessonProgress } from '@/domain/learningProgress';
import { createMajorHighApiV1 } from '@/api/majorHighApiV1';

const learnerApi = createMajorHighApiV1(supabase);

export interface LessonProgressRow {
  id: string;
  user_id: string;
  lesson_id: string;
  course_id: string;
  module_id: string;
  progress_percentage: number;
  last_position: number;
  time_spent: number;
  is_completed: boolean;
  completed_at: string | null;
  completion_date: string | null;
}

export interface CourseProgressDetail {
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
  completedLessonIds: string[];
  moduleProgress: Record<string, number>;
}

export async function getLessonProgress(
  authUserId: string,
  lessonId: string
): Promise<LessonProgressRow | null> {
  const { data, error } = await supabase
    .from('user_lesson_progress')
    .select('*')
    .eq('user_id', authUserId)
    .eq('lesson_id', lessonId)
    .maybeSingle();

  if (error) throw error;
  return data as LessonProgressRow | null;
}

export async function getCourseProgressDetail(
  authUserId: string,
  courseId: string,
  /** Quando definido, progresso considera só estes módulos (acesso parcial). */
  accessibleModuleIds?: string[] | null
): Promise<CourseProgressDetail> {
  const empty: CourseProgressDetail = {
    totalLessons: 0,
    completedLessons: 0,
    progressPercentage: 0,
    completedLessonIds: [],
    moduleProgress: {},
  };

  const { data: modulesData, error: modulesError } = await supabase
    .from('modules')
    .select('id, order')
    .eq('course_id', courseId)
    .order('order', { ascending: true });

  if (modulesError) throw modulesError;
  if (!modulesData?.length) return empty;

  let moduleIds = modulesData.map((m) => m.id);
  if (accessibleModuleIds != null && accessibleModuleIds.length > 0) {
    const allowed = new Set(accessibleModuleIds.map(String));
    moduleIds = moduleIds.filter((id) => allowed.has(String(id)));
  }
  if (moduleIds.length === 0) return empty;

  const { data: lessonsData, error: lessonsError } = await supabase
    .from('lessons')
    .select('id, module_id')
    .in('module_id', moduleIds);

  if (lessonsError) throw lessonsError;
  if (!lessonsData?.length) return empty;

  const lessonIds = lessonsData.map((l) => l.id);

  const { data: progressData, error: progressError } = await supabase
    .from('user_lesson_progress')
    .select('lesson_id, module_id, progress_percentage, is_completed, completed_at')
    .eq('user_id', authUserId)
    .in('lesson_id', lessonIds);

  if (progressError) throw progressError;

  const progressByLesson = new Map((progressData || []).map((p) => [String(p.lesson_id), p]));
  const completedLessonIds = (progressData || [])
    .filter((p) => p.is_completed)
    .map((p) => String(p.lesson_id));

  const moduleProgress: Record<string, number> = {};

  for (const moduleId of moduleIds) {
    const moduleLessons = lessonsData.filter((l) => l.module_id === moduleId);
    const total = moduleLessons.length;
    if (total === 0) {
      moduleProgress[moduleId] = 0;
      continue;
    }
    moduleProgress[moduleId] = aggregateLessonProgress(moduleLessons.map((lesson) => {
      const row = progressByLesson.get(String(lesson.id));
      return { progressPercentage: row?.progress_percentage ?? 0, completedAt: row?.completed_at ?? null };
    })).progressPercentage;
  }

  const totalLessons = lessonsData.length;
  const completedLessons = completedLessonIds.length;
  const progressPercentage = aggregateLessonProgress(lessonsData.map((lesson) => {
    const row = progressByLesson.get(String(lesson.id));
    return { progressPercentage: row?.progress_percentage ?? 0, completedAt: row?.completed_at ?? null };
  })).progressPercentage;

  return {
    totalLessons,
    completedLessons,
    progressPercentage,
    completedLessonIds,
    moduleProgress,
  };
}

export async function upsertLessonProgress(params: {
  authUserId: string;
  lessonId: string;
  moduleId: string;
  courseId: string;
  progressPercentage: number;
  lastPosition?: number;
  timeSpentDelta?: number;
  progressUpdateRef: string;
  clientType?: 'WEB' | 'MOBILE' | 'TEST' | 'UNKNOWN';
  sessionRef?: string;
}): Promise<LessonProgressRow> {
  const pct = Math.min(100, Math.max(0, Math.round(params.progressPercentage)));
  return learnerApi.saveLessonProgress({ progressUpdateRef: params.progressUpdateRef,
    lessonRef: params.lessonId, percentage: pct, positionSeconds: Math.max(0,Math.round(params.lastPosition ?? 0)),
    watchedSecondsDelta: Math.max(0,Math.round(params.timeSpentDelta ?? 0)), clientType: params.clientType ?? 'WEB',
    sessionRef: params.sessionRef }) as Promise<LessonProgressRow>;
}

export async function markLessonComplete(params: {
  authUserId: string;
  lessonId: string;
  moduleId: string;
  courseId: string;
  lastPosition?: number;
  progressUpdateRef: string;
}): Promise<LessonProgressRow> {
  return upsertLessonProgress({
    ...params,
    progressPercentage: 100,
    lastPosition: params.lastPosition,
  });
}

export async function syncModuleProgress(
  authUserId: string,
  moduleId: string,
  courseId: string,
  tenantId: string
): Promise<void> {
  const { data: lessonsData, error: lessonsError } = await supabase
    .from('lessons')
    .select('id')
    .eq('module_id', moduleId);

  if (lessonsError) throw lessonsError;

  const totalLessons = lessonsData?.length ?? 0;
  if (totalLessons === 0) return;

  const lessonIds = lessonsData!.map((l) => l.id);

  const { data: completedData, error: completedError } = await supabase
    .from('user_lesson_progress')
    .select('lesson_id')
    .eq('user_id', authUserId)
    .eq('is_completed', true)
    .in('lesson_id', lessonIds);

  if (completedError) throw completedError;

  const completedLessons = completedData?.length ?? 0;
  const progressPercentage = Math.round((completedLessons / totalLessons) * 100);
  const isCompleted = progressPercentage >= 100;
  const now = new Date().toISOString();

  const { data: existing } = await supabase
    .from('user_module_progress')
    .select('id, started_at')
    .eq('user_id', authUserId)
    .eq('module_id', moduleId)
    .maybeSingle();

  const row: Record<string, unknown> = {
    user_id: authUserId,
    module_id: moduleId,
    course_id: courseId,
    tenant_id: tenantId,
    total_lessons: totalLessons,
    completed_lessons: completedLessons,
    progress_percentage: progressPercentage,
    is_completed: isCompleted,
    updated_at: now,
  };

  if (!existing) {
    row.started_at = now;
    row.created_at = now;
  } else if (!existing.started_at) {
    row.started_at = now;
  }

  if (isCompleted) {
    row.completed_at = now;
  }

  const { error } = await supabase
    .from('user_module_progress')
    .upsert([row], { onConflict: 'user_id,module_id' });

  if (error) throw error;
}

export async function syncEnrollmentProgress(
  systemUserId: string,
  courseId: string,
  authUserId: string,
  tenantId?: string
): Promise<void> {
  const tid = tenantId || (await getTenantId());

  const { data: enrollment, error: fetchError } = await supabase
    .from('enrollments')
    .select('id, started_at')
    .eq('user_id', systemUserId)
    .eq('course_id', courseId)
    .eq('tenant_id', tid)
    .in('access_level', ['active', 'trial'])
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!enrollment) return;

  const { data: modulesData, error: modulesError } = await supabase
    .from('modules')
    .select('id')
    .eq('course_id', courseId);

  if (modulesError) throw modulesError;

  const moduleIds = modulesData?.map((m) => m.id) ?? [];
  if (moduleIds.length === 0) return;

  const { data: lessonsData, error: lessonsError } = await supabase
    .from('lessons')
    .select('id')
    .in('module_id', moduleIds);

  if (lessonsError) throw lessonsError;

  const totalLessons = lessonsData?.length ?? 0;
  const lessonIds = lessonsData?.map((l) => l.id) ?? [];

  let completedLessons = 0;
  if (lessonIds.length > 0) {
    const { data: completedData, error: completedError } = await supabase
      .from('user_lesson_progress')
      .select('lesson_id')
      .eq('user_id', authUserId)
      .eq('is_completed', true)
      .in('lesson_id', lessonIds);

    if (completedError) throw completedError;
    completedLessons = completedData?.length ?? 0;
  }

  const progressPercentage =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const now = new Date().toISOString();

  const updateData: Record<string, unknown> = {
    progress_percentage: progressPercentage,
    total_lessons: totalLessons,
    completed_lessons: completedLessons,
    last_accessed_at: now,
    updated_at: now,
  };

  if (!enrollment.started_at) {
    updateData.started_at = now;
  }

  if (progressPercentage >= 100) {
    updateData.completed_at = now;
  }

  const { error: updateError } = await supabase
    .from('enrollments')
    .update(updateData)
    .eq('id', enrollment.id);

  if (updateError) throw updateError;
}

/** Persiste progresso; o RPC sincroniza módulo + matrícula na mesma transação. */
export async function saveLessonProgressAndSync(params: {
  authUserId: string;
  systemUserId?: string;
  lessonId: string;
  moduleId: string;
  courseId: string;
  tenantId: string;
  progressPercentage: number;
  lastPosition?: number;
  timeSpentDelta?: number;
  forceComplete?: boolean;
  progressUpdateRef: string;
}): Promise<LessonProgressRow> {
  const result = params.forceComplete
    ? await markLessonComplete({
        authUserId: params.authUserId,
        lessonId: params.lessonId,
        moduleId: params.moduleId,
        courseId: params.courseId,
        lastPosition: params.lastPosition,
        progressUpdateRef: params.progressUpdateRef,
      })
    : await upsertLessonProgress({
        authUserId: params.authUserId,
        lessonId: params.lessonId,
        moduleId: params.moduleId,
        courseId: params.courseId,
        progressPercentage: params.progressPercentage,
        lastPosition: params.lastPosition,
        timeSpentDelta: params.timeSpentDelta,
        progressUpdateRef: params.progressUpdateRef,
      });

  return result;
}
